import { useEffect } from 'react';
import { Outlet, RouteObject } from 'react-router-dom';
import { AppVersionChecker } from 'shared/app/AppVersionChecker';
import { LocalStore } from 'shared/helpers/Store';
import { ONBOARDING_FIRST_LOAD_TOUR_STORAGE_KEY } from 'shared/modules/onboarding/data/OnboardingConstants';
import { useFlag } from 'shared/settings/FeatureToggles';
import { listenToLoadEvents } from './data/LoadEvents';
import { LoadsContextProvider } from './data/LoadsContext';
import { LoadsPageContextProvider } from './loads-page/data/LoadsPageParamsContext';
import { PermissionView } from 'shared/modules/permission/core/PermissionView';
import { LoadsImportZone } from './core/LoadsImportZone';
import { lazyImport } from 'shared/routing/LazyRoute';

const AddLoadPage = lazyImport(() => import('./LoadsPages'), 'AddLoadPage');
const LoadInvoicePreviewPage = lazyImport(
  () => import('./LoadsPages'),
  'LoadInvoicePreviewPage',
);
const EditLoadPage = lazyImport(() => import('./LoadsPages'), 'EditLoadPage');
const LoadsPage = lazyImport(() => import('./LoadsPages'), 'LoadsPage');
const ViewLoadPage = lazyImport(() => import('./LoadsPages'), 'ViewLoadPage');
const ViewLoadPagePrintPage = lazyImport(
  () => import('./LoadsPages'),
  'ViewLoadPagePrintPage',
);

export function LoadsContainerOutlet() {
  useEffect(() => {
    listenToLoadEvents((event) => {
      if (event === 'created' || event === 'imported') {
        LocalStore.set(ONBOARDING_FIRST_LOAD_TOUR_STORAGE_KEY, 'pending');
      }
    });
  }, []);

  return (
    <LoadsContextProvider>
      <AppVersionChecker />
      <PermissionView name="loads" permission="can_view">
        <Outlet />
      </PermissionView>
    </LoadsContextProvider>
  );
}

export const loadsRoutes: RouteObject[] = [
  {
    path: 'loads',
    element: <LoadsContainerOutlet />,
    children: [
      { index: true, element: <LoadsPageRoute /> },
      {
        path: 'add',
        element: <AddLoadPage />,
      },
      {
        path: 'invoice-preview',
        element: <LoadOutlet />,
        children: [
          {
            index: true,
            element: <LoadInvoicePreviewPage />,
          },
        ],
      },
      {
        path: ':loadGUID',
        element: <LoadOutlet />,
        children: [
          {
            path: 'edit',
            element: <EditLoadPage />,
          },
          {
            path: 'print',
            element: <ViewLoadPagePrintPage />,
          },
          {
            path: ':action?/:actionGUID?',
            element: <ViewLoadPage />,
          },
        ],
      },
    ],
  },
];

function LoadOutlet() {
  return (
    <LoadsPageContextProvider shouldPersistParams={true}>
      <Outlet />
    </LoadsPageContextProvider>
  );
}

function LoadsPageRoute() {
  const shouldShowDropzone = useFlag('ctms_loads_dropzone');

  return (
    <LoadsPageContextProvider shouldPersistParams={true}>
      {shouldShowDropzone ? (
        <LoadsImportZone>
          <LoadsPage />
        </LoadsImportZone>
      ) : (
        <LoadsPage />
      )}
    </LoadsPageContextProvider>
  );
}
